/**
 * Sales → B2B orders: orders for customers such as Peeko and Ozy, from order to dispatch, Tally invoice and payment, with what
 * is due against their terms. Never part of overall revenue. Margin shows to admins only; the warehouse role only dispatches.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Download, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useCountry } from '../contexts/CountryContext';
import { useChannels } from '../hooks/useChannels';
import { isAdmin, isWarehouseRole } from '../lib/permissions';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { LoadError, errorMessage } from '../components/LoadError';
import { downloadCsv, indiaToday, number, wholeMoney } from '../components/sales/format';
import { useDebouncedValue } from '../components/sales/useAbortableGet';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api/b2b-orders`;
const SELECT = 'h-9 w-full rounded-md border border-gray-300 bg-white px-2 text-sm';
const STATUS_LABEL = { DRAFT: 'Not dispatched', DISPATCHED: 'Dispatched', INVOICED: 'Invoiced', PART_PAID: 'Part paid', PAID: 'Paid', CANCELLED: 'Cancelled' };
const STATUS_TONE = { DRAFT: 'bg-sky-50 text-sky-800', DISPATCHED: 'bg-amber-50 text-amber-800', INVOICED: 'bg-violet-50 text-violet-800', PART_PAID: 'bg-orange-50 text-orange-800', PAID: 'bg-green-50 text-green-800', CANCELLED: 'bg-gray-100 text-gray-500' };
const MODES = { NEFT: 'NEFT', RTGS: 'RTGS', UPI: 'UPI', CHEQUE: 'Cheque', OTHER: 'Other' };

const Field = ({ id, label, children, hint }) => (
  <div className="space-y-1"><Label htmlFor={id}>{label}</Label>{children}{hint && <p className="text-[11px] text-gray-500">{hint}</p>}</div>
);

const useSubmit = (onClose, onDone) => {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const run = async (call, message) => {
    setSaving(true);
    setError('');
    try {
      await call();
      toast.success(message);
      onClose();
      onDone();
    } catch (err) {
      setError(errorMessage(err, 'Could not save this'));
    } finally {
      setSaving(false);
    }
  };
  return { saving, error, run };
};

const Shell = ({ title, description, onClose, children, wide }) => (
  <Dialog open onOpenChange={next => { if (!next) onClose(); }}>
    <DialogContent className={`max-h-[90vh] overflow-y-auto ${wide ? 'max-w-3xl' : ''}`}>
      <DialogHeader><DialogTitle>{title}</DialogTitle>{description && <DialogDescription>{description}</DialogDescription>}</DialogHeader>
      {children}
    </DialogContent>
  </Dialog>
);

const Footer = ({ onClose, saving, error, label, testid, destructive }) => (
  <>
    {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
    <DialogFooter>
      <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
      <Button type="submit" variant={destructive ? 'destructive' : 'default'} disabled={saving} data-testid={testid}>{saving ? 'Saving…' : label}</Button>
    </DialogFooter>
  </>
);

const NewOrderDialog = ({ customers, country, onClose, onDone }) => {
  const [form, setForm] = useState({ customer: customers[0]?.code || '', order_no: '', order_date: indiaToday(), note: '', lines: [{ sku_code: '', qty: 1, unit_price: '', hint: '' }] });
  const [skus, setSkus] = useState([]);
  const { saving, error, run } = useSubmit(onClose, onDone);
  useEffect(() => {
    const controller = new AbortController();
    axios.get(`${process.env.REACT_APP_BACKEND_URL || ''}/api/skus?country=${country}`, { signal: controller.signal })
      .then(res => setSkus(Array.isArray(res.data) ? res.data : (res.data?.items || []))).catch(() => {});
    return () => controller.abort();
  }, [country]);
  const set = patch => setForm(current => ({ ...current, ...patch }));
  const setLine = (index, patch) => setForm(current => ({ ...current, lines: current.lines.map((line, i) => (i === index ? { ...line, ...patch } : line)) }));
  // A price the person has not typed follows the SKU, customer and date; a typed price is never replaced.
  const suggest = async index => {
    const line = form.lines[index];
    const code = line.sku_code.trim();
    if (!code || !form.customer) return;
    try {
      const { data } = await axios.get(`${API}/price`, { params: { customer: form.customer, sku: code, country, day: form.order_date } });
      setLine(index, { hint: data.basis, ...(line.unit_price === '' || line.auto ? { unit_price: data.price ?? '', auto: Boolean(data.price) } : {}) });
    } catch { /* a hint only */ }
  };
  const customerKey = `${form.customer}|${form.order_date}`;
  useEffect(() => {
    form.lines.forEach((line, index) => { if (line.sku_code.trim() && (line.auto || line.unit_price === '')) suggest(index); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerKey]);
  const packOf = code => skus.find(s => s.sku_code === code.trim() && s.bundle_of);
  const total = form.lines.reduce((sum, line) => sum + Number(line.qty || 0) * Number(line.unit_price || 0), 0);
  const submit = event => {
    event.preventDefault();
    run(() => axios.post(API, { country, customer: form.customer, order_no: form.order_no, order_date: form.order_date, note: form.note,
      lines: form.lines.map(line => ({ sku_code: line.sku_code.trim(), qty: Number(line.qty), unit_price: Number(line.unit_price) })) }), 'Order saved');
  };
  return (
    <Shell title="New B2B order" description="Stock moves when you dispatch it, not now." onClose={onClose} wide>
      <form onSubmit={submit} className="space-y-4" data-testid="b2b-form">
        <div className="grid gap-3 sm:grid-cols-3">
          <Field id="b2b-customer" label="Customer">
            <select id="b2b-customer" className={SELECT} value={form.customer} onChange={e => set({ customer: e.target.value })} required data-testid="b2b-customer">
              {customers.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
            </select>
          </Field>
          <Field id="b2b-no" label="Their order number"><Input id="b2b-no" value={form.order_no} onChange={e => set({ order_no: e.target.value })} maxLength={80} /></Field>
          <Field id="b2b-date" label="Order date"><Input id="b2b-date" type="date" max={indiaToday()} value={form.order_date} onChange={e => set({ order_date: e.target.value })} required /></Field>
        </div>
        <fieldset className="space-y-2">
          <legend className="text-sm font-medium">Products (price each, excluding GST)</legend>
          <datalist id="b2b-skus">{skus.map(s => <option key={s.sku_code} value={s.sku_code}>{s.product_name}</option>)}</datalist>
          {form.lines.map((line, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <div key={index} className="space-y-0.5">
              <div className="flex items-center gap-2">
                <Input list="b2b-skus" aria-label="SKU" value={line.sku_code} onChange={e => setLine(index, { sku_code: e.target.value })} onBlur={() => suggest(index)} placeholder="SKU" required className="flex-1" data-testid={`b2b-sku-${index}`} />
                <Input aria-label="Units" type="number" min="1" step="1" value={line.qty} onChange={e => setLine(index, { qty: e.target.value })} required className="w-20" data-testid={`b2b-qty-${index}`} />
                <Input aria-label="Price each" type="number" min="0.01" step="0.01" value={line.unit_price} onChange={e => setLine(index, { unit_price: e.target.value, auto: false })} placeholder="₹ price" required className="w-28" data-testid={`b2b-price-${index}`} />
                <Button type="button" variant="ghost" size="sm" aria-label="Remove product" disabled={form.lines.length === 1} onClick={() => set({ lines: form.lines.filter((_, i) => i !== index) })}><Trash2 className="h-4 w-4" aria-hidden="true" /></Button>
              </div>
              {packOf(line.sku_code) && <p className="text-[11px] font-medium text-amber-800" data-testid={`b2b-bundle-${index}`}>A bundle of {packOf(line.sku_code).bundle_qty || 1} × {packOf(line.sku_code).bundle_of}: enter the number of bundles and the price per bundle.</p>}
              {line.hint && <p className="text-[11px] text-gray-500">{line.hint}</p>}
            </div>
          ))}
          <Button type="button" variant="outline" size="sm" onClick={() => set({ lines: [...form.lines, { sku_code: '', qty: 1, unit_price: '', hint: '' }] })}><Plus className="mr-1 h-4 w-4" aria-hidden="true" />Add product</Button>
          <p className="text-sm font-medium">Order value: {wholeMoney(total)} excl. GST</p>
        </fieldset>
        <Field id="b2b-note" label="Note"><Input id="b2b-note" value={form.note} onChange={e => set({ note: e.target.value })} maxLength={1000} /></Field>
        <Footer onClose={onClose} saving={saving} error={error} label="Save order" testid="b2b-save" />
      </form>
    </Shell>
  );
};

const DispatchDialog = ({ order, country, onClose, onDone }) => {
  const master = useChannels();
  const [form, setForm] = useState({ date: indiaToday(), from_bin: 'warehouse', transporter: '', reference: '' });
  const { saving, error, run } = useSubmit(onClose, onDone);
  const set = patch => setForm(current => ({ ...current, ...patch }));
  return (
    <Shell title={`Dispatch ${order.id}`} description={`${number(order.units)} units leave the bin now, for ${order.customer_name}.`} onClose={onClose}>
      <form onSubmit={e => { e.preventDefault(); run(() => axios.post(`${API}/${order.id}/dispatch`, { country, ...form }), 'Dispatched'); }} className="space-y-3">
        <ul className="rounded-lg border border-gray-200 bg-gray-50 p-2 text-sm" data-testid="b2b-pick-list">
          {order.lines.map(line => <li key={line.line_id} className="flex justify-between gap-3"><span><span className="font-mono text-xs">{line.sku_code}</span> {line.product_name}{line.entered_sku ? ` (from ${line.entered_qty} × ${line.entered_sku})` : ''}</span><span className="font-medium tabular-nums">{number(line.qty)}</span></li>)}
        </ul>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field id="d-date" label="Dispatch date"><Input id="d-date" type="date" min={order.order_date} max={indiaToday()} value={form.date} onChange={e => set({ date: e.target.value })} required /></Field>
          <Field id="d-bin" label="From bin"><select id="d-bin" className={SELECT} value={form.from_bin} onChange={e => set({ from_bin: e.target.value })}>
            {(master.bins.length ? master.bins : [{ key: 'warehouse', label: 'Warehouse' }]).map(b => <option key={b.key} value={b.key}>{b.label}</option>)}
          </select></Field>
          <Field id="d-tr" label="Transporter"><Input id="d-tr" value={form.transporter} onChange={e => set({ transporter: e.target.value })} maxLength={120} /></Field>
          <Field id="d-ref" label="LR or docket number"><Input id="d-ref" value={form.reference} onChange={e => set({ reference: e.target.value })} maxLength={120} /></Field>
        </div>
        <Footer onClose={onClose} saving={saving} error={error} label="Dispatch" testid="b2b-dispatch-save" />
      </form>
    </Shell>
  );
};

const InvoiceDialog = ({ order, country, onClose, onDone }) => {
  const [form, setForm] = useState({ number: order.invoice?.number || '', date: order.invoice?.date || indiaToday(), amount: order.invoice?.amount ?? '' });
  const { saving, error, run } = useSubmit(onClose, onDone);
  const set = patch => setForm(current => ({ ...current, ...patch }));
  return (
    <Shell title={`Tally invoice · ${order.id}`} description={`Order value ${wholeMoney(order.value)} excl. GST. Enter the invoice as billed in Tally, including GST. Dues run from this.`} onClose={onClose}>
      <form onSubmit={e => { e.preventDefault(); run(() => axios.post(`${API}/${order.id}/invoice`, { country, ...form, amount: Number(form.amount) }), 'Invoice saved'); }} className="space-y-3">
        <Field id="i-no" label="Tally invoice number"><Input id="i-no" value={form.number} onChange={e => set({ number: e.target.value })} required maxLength={60} data-testid="b2b-invoice-no" /></Field>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field id="i-date" label="Invoice date"><Input id="i-date" type="date" min={order.dispatch?.date} max={indiaToday()} value={form.date} onChange={e => set({ date: e.target.value })} required /></Field>
          <Field id="i-amount" label="Amount, incl. GST"><Input id="i-amount" type="number" min="0.01" step="0.01" value={form.amount} onChange={e => set({ amount: e.target.value })} required data-testid="b2b-invoice-amount" /></Field>
        </div>
        <Footer onClose={onClose} saving={saving} error={error} label="Save invoice" testid="b2b-invoice-save" />
      </form>
    </Shell>
  );
};

const PaymentDialog = ({ order, country, onClose, onDone }) => {
  const [form, setForm] = useState({ date: indiaToday(), amount: order.balance ?? '', mode: 'NEFT', reference: '' });
  const { saving, error, run } = useSubmit(onClose, onDone);
  const set = patch => setForm(current => ({ ...current, ...patch }));
  const paid = order.status === 'PAID';
  const remove = async payment => {
    // eslint-disable-next-line no-alert
    const reason = window.prompt(`Remove the payment of ${wholeMoney(payment.amount)}? Why?`);
    if (!reason) return;
    run(() => axios.post(`${API}/${order.id}/payments/${payment.id}/void`, { country, reason }), 'Payment removed');
  };
  return (
    <Shell title={`Payments · ${order.invoice?.number}`} description={`${wholeMoney(order.balance)} left of ${wholeMoney(order.invoice?.amount)}.`} onClose={onClose}>
      <form onSubmit={e => { e.preventDefault(); run(() => axios.post(`${API}/${order.id}/payments`, { country, ...form, amount: Number(form.amount) }), 'Payment recorded'); }} className="space-y-3">
        {(order.payments || []).length > 0 && (
          <ul className="space-y-1 text-sm">
            {order.payments.map(p => (p.voided
              ? <li key={p.id} className="text-gray-500"><s>{p.date}: {wholeMoney(p.amount)}</s> removed: {p.voided.reason}</li>
              : <li key={p.id} className="flex items-center justify-between gap-2"><span>{p.date}: {wholeMoney(p.amount)} {p.mode || ''} {p.reference || ''}</span><Button type="button" variant="ghost" size="sm" className="h-7 text-xs" onClick={() => remove(p)}>Remove</Button></li>))}
          </ul>
        )}
        {!paid && <div className="grid gap-3 sm:grid-cols-2">
          <Field id="p-date" label="Date received"><Input id="p-date" type="date" min={order.invoice?.date} max={indiaToday()} value={form.date} onChange={e => set({ date: e.target.value })} required /></Field>
          <Field id="p-amount" label="Amount"><Input id="p-amount" type="number" min="0.01" step="0.01" value={form.amount} onChange={e => set({ amount: e.target.value })} required data-testid="b2b-payment-amount" /></Field>
          <Field id="p-mode" label="Mode"><select id="p-mode" className={SELECT} value={form.mode} onChange={e => set({ mode: e.target.value })}>{Object.entries(MODES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></Field>
          <Field id="p-ref" label="Reference"><Input id="p-ref" value={form.reference} onChange={e => set({ reference: e.target.value })} maxLength={120} /></Field>
        </div>}
        {paid
          ? <>{error && <p role="alert" className="text-sm text-red-700">{error}</p>}<DialogFooter><Button type="button" variant="outline" onClick={onClose}>Close</Button></DialogFooter></>
          : <Footer onClose={onClose} saving={saving} error={error} label="Record payment" testid="b2b-payment-save" />}
      </form>
    </Shell>
  );
};

const ReasonDialog = ({ title, description, action, label, country, onClose, onDone, done }) => {
  const [reason, setReason] = useState('');
  const { saving, error, run } = useSubmit(onClose, onDone);
  return (
    <Shell title={title} description={description} onClose={onClose}>
      <form onSubmit={e => { e.preventDefault(); run(() => axios.post(action, { country, reason }), done); }} className="space-y-3">
        <Field id="r-why" label="Why"><Input id="r-why" value={reason} onChange={e => setReason(e.target.value)} required maxLength={500} data-testid="b2b-reason" /></Field>
        <Footer onClose={onClose} saving={saving} error={error} label={label} testid="b2b-reason-save" destructive />
      </form>
    </Shell>
  );
};

const productText = line => `${line.sku_code} × ${line.qty}${line.entered_sku ? ` (${line.entered_qty} × ${line.entered_sku})` : ''}`;

const OrdersTable = ({ country, office, showMargin, stamp, onAct }) => {
  const master = useChannels();
  const customers = useMemo(() => master.all.filter(c => c.type === 'b2b_customer'), [master.all]);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [state, setState] = useState('all');
  const [customer, setCustomer] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const query = useDebouncedValue(search, 300);
  useEffect(() => { setPage(1); }, [state, customer, query, country]);
  useEffect(() => {
    const controller = new AbortController();
    setError(null);
    axios.get(API, { params: { country, state, customer: customer || undefined, search: query || undefined, page }, signal: controller.signal })
      .then(res => {
        setData(res.data);
        const last = Math.max(1, Math.ceil(res.data.total / res.data.page_size));
        if (page > last) setPage(last);
      })
      .catch(err => { if (!axios.isCancel(err)) setError(err); });
    return () => controller.abort();
  }, [country, state, customer, query, page, stamp]);
  if (error) return <LoadError error={error} />;
  const pages = data ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1;
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        <Input aria-label="Search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search order, invoice, SKU" className="h-9 w-64" />
        <select aria-label="Customer" className="h-9 rounded-md border border-gray-300 bg-white px-2 text-sm" value={customer} onChange={e => setCustomer(e.target.value)}>
          <option value="">All customers</option>{customers.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
        </select>
        <select aria-label="Status" className="h-9 rounded-md border border-gray-300 bg-white px-2 text-sm" value={state} onChange={e => setState(e.target.value)}>
          <option value="all">All statuses</option>{Object.entries(STATUS_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>
      <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
        <table className="w-full text-sm" data-testid="b2b-table">
          <thead className="bg-gray-50 text-left text-xs text-gray-600">
            <tr><th className="p-3">Order</th><th className="p-3">Customer</th><th className="p-3">Date</th><th className="p-3 text-right">Units</th><th className="p-3 text-right">Value excl. GST</th>{showMargin && <th className="p-3 text-right">Margin</th>}<th className="p-3">Invoice</th><th className="p-3 text-right">Balance</th><th className="p-3">Status</th><th className="p-3" /></tr>
          </thead>
          <tbody>
            {!data && <tr><td colSpan="10" className="p-6 text-center text-gray-500">Loading…</td></tr>}
            {data && !data.items.length && <tr><td colSpan="10" className="p-6 text-center text-gray-500">No orders yet.</td></tr>}
            {data?.items.map(order => (
              <tr key={order.id} className="border-t border-gray-100 align-top">
                <td className="p-3"><span className="font-mono text-xs">{order.id}</span>{order.order_no && <div className="text-xs text-gray-500">{order.order_no}</div>}</td>
                <td className="p-3">{order.customer_name}<div className="font-mono text-xs text-gray-500">{order.lines.map(productText).join(', ')}</div></td>
                <td className="p-3 whitespace-nowrap">{order.order_date}{order.dispatch?.date && <div className="text-xs text-gray-500">Sent {order.dispatch.date}</div>}</td>
                <td className="p-3 text-right tabular-nums">{number(order.units)}</td>
                <td className="p-3 text-right tabular-nums">{wholeMoney(order.value)}</td>
                {showMargin && <td className="p-3 text-right tabular-nums">{order.margin == null ? <span title={order.uncosted_units ? `${order.uncosted_units} units have no purchase cost` : undefined}>{order.uncosted_units ? 'No cost' : '—'}</span> : wholeMoney(order.margin)}</td>}
                <td className="p-3 whitespace-nowrap">{order.invoice ? <>{order.invoice.number}<div className="text-xs text-gray-500">{wholeMoney(order.invoice.amount)} · due {order.due_date}</div></> : '—'}</td>
                <td className="p-3 text-right tabular-nums">{order.invoice ? wholeMoney(order.balance) : '—'}{order.days_late > 0 && <div className="text-xs font-medium text-red-700">{order.days_late} days late</div>}</td>
                <td className="p-3"><span className={`rounded px-1.5 py-0.5 text-xs ${STATUS_TONE[order.status] || ''}`}>{STATUS_LABEL[order.status] || order.status}</span></td>
                <td className="p-3 whitespace-nowrap text-right space-x-1">
                  {order.status === 'DRAFT' && <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => onAct('dispatch', order)} data-testid={`b2b-dispatch-${order.id}`}>Dispatch</Button>}
                  {office && order.status === 'DRAFT' && <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => onAct('cancel', order)}>Cancel</Button>}
                  {office && order.status === 'DISPATCHED' && <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => onAct('invoice', order)} data-testid={`b2b-invoice-${order.id}`}>Add invoice</Button>}
                  {office && order.status === 'DISPATCHED' && <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => onAct('undo', order)}>Undo dispatch</Button>}
                  {office && order.status === 'INVOICED' && <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => onAct('invoice', order)}>Edit invoice</Button>}
                  {office && ['INVOICED', 'PART_PAID'].includes(order.status) && <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => onAct('payment', order)} data-testid={`b2b-payment-${order.id}`}>Payment</Button>}
                  {office && order.status === 'PAID' && <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => onAct('payment-view', order)}>Payments</Button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {pages > 1 && <div className="flex items-center justify-end gap-2 text-sm"><Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</Button><span>Page {page} of {pages}</span><Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage(page + 1)}>Next</Button></div>}
    </div>
  );
};

const DuesPanel = ({ country, stamp }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    const controller = new AbortController();
    setError(null);
    axios.get(`${API}/dues`, { params: { country }, signal: controller.signal })
      .then(res => setData(res.data)).catch(err => { if (!axios.isCancel(err)) setError(err); });
    return () => controller.abort();
  }, [country, stamp]);
  if (error) return <LoadError error={error} />;
  if (!data) return <p className="p-6 text-center text-gray-500">Loading…</p>;
  const exportDues = () => downloadCsv([['Customer', 'Invoice', 'Invoice date', 'Invoice amount', 'Paid', 'Balance', 'Due date', 'Days late'],
    ...data.invoices.map(o => [o.customer_name, o.invoice?.number, o.invoice?.date, o.invoice?.amount, o.paid, o.balance, o.due_date, o.days_late])], 'b2b_payment_dues.csv');
  const noTerms = data.invoices.filter(o => o.no_terms).length;
  return (
    <div className="space-y-4" data-testid="b2b-dues">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-gray-600">Unpaid invoices as billed in Tally, including GST. Due dates come from the payment terms on the invoice date. "From delivery" counts from the dispatch date, and "settlement cycle" from the invoice date.</p>
        <Button variant="outline" size="sm" onClick={exportDues} disabled={!data.invoices.length}><Download className="mr-1 h-4 w-4" aria-hidden="true" />Export</Button>
      </div>
      {noTerms > 0 && <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">{noTerms} invoice{noTerms === 1 ? ' has' : 's have'} no credit days in the customer's terms, so {noTerms === 1 ? 'it is' : 'they are'} due from the invoice date. Add terms in Settings → Channels & customers.</p>}
      <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-xs text-gray-600"><tr><th className="p-3">Customer</th><th className="p-3 text-right">Invoices</th>{data.buckets.map(b => <th key={b.key} className="p-3 text-right">{b.label}</th>)}<th className="p-3 text-right">Total due</th></tr></thead>
          <tbody>
            {!data.customers.length && <tr><td colSpan="8" className="p-6 text-center text-gray-500">Nothing is due.</td></tr>}
            {data.customers.map(row => (
              <tr key={row.customer} className="border-t border-gray-100">
                <td className="p-3">{row.name}</td><td className="p-3 text-right tabular-nums">{number(row.invoices)}</td>
                {data.buckets.map(b => <td key={b.key} className={`p-3 text-right tabular-nums ${row[b.key] && b.key !== 'not_due' ? 'text-red-700' : ''}`}>{row[b.key] ? wholeMoney(row[b.key]) : '—'}</td>)}
                <td className="p-3 text-right font-medium tabular-nums">{wholeMoney(row.balance)}</td>
              </tr>
            ))}
            {data.customers.length > 1 && (
              <tr className="border-t border-gray-200 font-medium"><td className="p-3">Total</td><td className="p-3 text-right tabular-nums">{number(data.invoices.length)}</td>{data.buckets.map(b => <td key={b.key} className="p-3 text-right tabular-nums">{data.totals[b.key] ? wholeMoney(data.totals[b.key]) : '—'}</td>)}<td className="p-3 text-right tabular-nums">{wholeMoney(data.totals.balance)}</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {data.invoices.length > 0 && (
        <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs text-gray-600"><tr><th className="p-3">Customer</th><th className="p-3">Invoice</th><th className="p-3">Invoice date</th><th className="p-3">Due</th><th className="p-3 text-right">Days late</th><th className="p-3 text-right">Balance</th></tr></thead>
            <tbody>{data.invoices.map(o => <tr key={o.id} className="border-t border-gray-100"><td className="p-3">{o.customer_name}</td><td className="p-3">{o.invoice?.number}</td><td className="p-3">{o.invoice?.date}</td><td className="p-3">{o.due_date}</td><td className={`p-3 text-right tabular-nums ${o.days_late ? 'font-medium text-red-700' : ''}`}>{o.days_late || '—'}</td><td className="p-3 text-right tabular-nums">{wholeMoney(o.balance)}</td></tr>)}</tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default function B2bOrdersPage() {
  const { user } = useAuth();
  const { country } = useCountry();
  const master = useChannels();
  const customers = useMemo(() => master.all.filter(c => c.type === 'b2b_customer' && c.status === 'active'), [master.all]);
  const [stamp, setStamp] = useState(0);
  const [creating, setCreating] = useState(false);
  const [act, setAct] = useState(null);
  const office = !isWarehouseRole(user);
  const refresh = useCallback(() => setStamp(value => value + 1), []);
  const close = () => setAct(null);
  const onAct = (kind, order) => setAct({ kind, order });
  const common = act ? { order: act.order, country, onClose: close, onDone: refresh } : {};
  return (
    <div className="space-y-4 p-4 sm:p-6" data-testid="b2b-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">B2B orders</h1>
          <p className="text-sm text-gray-600">Orders for B2B customers, from dispatch to Tally invoice and payment. Counted as B2B revenue on the dispatch date, never in overall revenue.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={refresh} aria-label="Refresh"><RefreshCw className="h-4 w-4" aria-hidden="true" /></Button>
          {office && <Button size="sm" disabled={!customers.length} title={customers.length ? undefined : 'Add an active B2B customer first'} onClick={() => setCreating(true)} data-testid="b2b-new"><Plus className="mr-1 h-4 w-4" aria-hidden="true" />New order</Button>}
        </div>
      </div>
      {!customers.length && <p className="rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-sm text-sky-900" data-testid="b2b-no-customers">No active B2B customers yet. Add Peeko and Ozy in Settings → Channels & customers, with type B2B customer and status Active.</p>}
      <Tabs defaultValue="orders">
        <TabsList>
          <TabsTrigger value="orders">Orders</TabsTrigger>
          <TabsTrigger value="dues" data-testid="b2b-tab-dues">Payment dues</TabsTrigger>
        </TabsList>
        <TabsContent value="orders" className="mt-3"><OrdersTable country={country} office={office} showMargin={isAdmin(user)} stamp={stamp} onAct={onAct} /></TabsContent>
        <TabsContent value="dues" className="mt-3"><DuesPanel country={country} stamp={stamp} /></TabsContent>
      </Tabs>
      {creating && <NewOrderDialog customers={customers} country={country} onClose={() => setCreating(false)} onDone={refresh} />}
      {act?.kind === 'dispatch' && <DispatchDialog {...common} />}
      {act?.kind === 'invoice' && <InvoiceDialog {...common} />}
      {(act?.kind === 'payment' || act?.kind === 'payment-view') && <PaymentDialog {...common} />}
      {act?.kind === 'cancel' && <ReasonDialog title={`Cancel ${act.order.id}?`} description="The order stays on record as cancelled." action={`${API}/${act.order.id}/cancel`} label="Cancel order" done="Order cancelled" country={country} onClose={close} onDone={refresh} />}
      {act?.kind === 'undo' && <ReasonDialog title={`Undo dispatch of ${act.order.id}?`} description="The stock goes back to its bin and the order returns to not dispatched." action={`${API}/${act.order.id}/undo-dispatch`} label="Undo dispatch" done="Dispatch undone" country={country} onClose={close} onDone={refresh} />}
    </div>
  );
}
